import React, { useMemo } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getComplianceReport } from '../../data/reportHelpers';
import { getStatusColor } from '../../data/helpers';
import SummaryStatBar from './SummaryStatBar';
import ExportBar from './ExportBar';

const { FiShield, FiCheckCircle, FiAlertTriangle, FiClock, FiXCircle } = FiIcons;

const COLUMNS = [
  { key: 'learnerName', label: 'Learner' },
  { key: 'department', label: 'Department' },
  { key: 'manager', label: 'Manager' },
  { key: 'training', label: 'Required Training' },
  { key: 'assignedDate', label: 'Assigned' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'completionDate', label: 'Completed' },
  { key: 'status', label: 'Status' },
];

const ComplianceReport = ({ filters, raw }) => {
  const data = useMemo(() => getComplianceReport(raw, filters), [raw, filters]);

  const counts = {
    compliant: data.filter(r => r.status === 'Compliant').length,
    overdue: data.filter(r => r.status === 'Overdue').length,
    atRisk: data.filter(r => r.status === 'At Risk').length,
    inProgress: data.filter(r => r.status === 'In Progress').length,
    notStarted: data.filter(r => r.status === 'Not Started').length,
  };
  const rate = Math.round((counts.compliant / (data.length || 1)) * 100);

  const stats = [
    { label: 'Total Records', value: data.length, icon: FiShield, iconColor: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Compliant', value: counts.compliant, icon: FiCheckCircle, iconColor: 'bg-emerald-50 text-emerald-600', border: 'border-emerald-100' },
    { label: 'Overdue', value: counts.overdue, icon: FiXCircle, iconColor: 'bg-red-50 text-red-600', border: 'border-red-100' },
    { label: 'At Risk', value: counts.atRisk, icon: FiAlertTriangle, iconColor: 'bg-orange-50 text-orange-600', border: 'border-orange-100' },
    { label: 'In Progress', value: counts.inProgress, icon: FiClock, iconColor: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Compliance Rate', value: `${rate}%`, icon: FiCheckCircle, iconColor: 'bg-teal-50 text-teal-600', border: 'border-teal-100', valueColor: rate >= 80 ? 'text-emerald-600' : rate >= 60 ? 'text-amber-600' : 'text-red-600' },
  ];

  return (
    <div className="space-y-4">
      <SummaryStatBar stats={stats} />
      <ExportBar reportTitle="Compliance Report" data={data} columns={COLUMNS} summaryStats={stats.map(s => ({ label: s.label, value: s.value }))} rowCount={data.length} />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <th className="p-4">Learner</th>
                <th className="p-4">Department</th>
                <th className="p-4">Manager</th>
                <th className="p-4">Required Training</th>
                <th className="p-4">Due Date</th>
                <th className="p-4">Completed</th>
                <th className="p-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {data.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 font-medium text-slate-800">{row.learnerName}</td>
                  <td className="p-4 text-slate-600 text-xs">{row.department}</td>
                  <td className="p-4 text-slate-600 text-xs">{row.manager}</td>
                  <td className="p-4 text-slate-700 text-xs max-w-40 truncate">{row.training}</td>
                  <td className="p-4 text-xs text-slate-500">{row.dueDate}</td>
                  <td className="p-4 text-xs text-slate-500">{row.completionDate || '—'}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(row.status)}`}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">{data.length} records</div>
      </div>
    </div>
  );
};

export default ComplianceReport;